import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { UniversalLoadingState, Card, Button, NotificationCard, UniversalPage } from '@sighthost/ui';
import {
  adminFinancialsService,
  type IncomeStatement,
} from '@/domains/financials/services/adminFinancialsService';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { IncomeStatementCard } from './components';

function YearlyReportPageBody() {
  const [data, setData] = useState<IncomeStatement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminFinancialsService.getYearlyReport(3).then((d) => setData(d.reverse())).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }, []);

  if (error) return <NotificationCard tone="danger" title="Could not load yearly report" body={error} />;
  if (!data) return <UniversalLoadingState variant="spinner" caption="Aggregating year-by-year…" />;

  function exportCsv() {
    const header = [
      'Year', 'Hosting', 'Domains', 'Other', 'Revenue',
      'WHM hosting', 'domains.co.za top-up', 'PayFast fees',
      'Internet', 'Infrastructure', 'Tooling', 'Salary',
      'Marketing', 'Other expenses', 'Total expenses', 'Net',
    ];
    const dataRows = data!.map((y) => [
      y.period_label, y.revenue.hosting, y.revenue.domains, y.revenue.other, y.revenue.total,
      y.expense.whm_hosting, y.expense.domains_topup, y.expense.payfast_fees,
      y.expense.internet, y.expense.infrastructure, y.expense.tooling,
      y.expense.salary, y.expense.marketing, y.expense.other, y.expense.total, y.net,
    ]);
    exportRowsAsCsv(`yearly-report-${todayStamp()}.csv`, header, dataRows);
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Calendar-year totals (revenue from <code>payment.paid_at</code>, expenses from
          <code> expense.incurred_on</code>). Shown newest first. Year still in progress shows
          partial-year totals.
        </p>
      </Card>

      {data.map((y) => {
        const prevYear = data[data.indexOf(y) + 1]; // older year for comparison
        return (
          <IncomeStatementCard
            key={y.period_start}
            statement={y}
            comparisonStatement={prevYear}
            comparisonLabel={prevYear ? prevYear.period_label : undefined}
          />
        );
      })}
    </div>
  );
}

/**
 * These five pages previously rendered NO page header at all — no title, no
 * breadcrumbs — because FinancialReportsLayout supplied a nav row and nothing
 * else. That was a §4.4 violation hiding inside a §4.1 one, and it is why no
 * financial report had a back link.
 */
export default function YearlyReportPage() {
  return (
    <UniversalPage
      title="Yearly report"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Financials', href: '/admin/financials' },
        { label: 'Yearly report' },
      ]}
    >
      <YearlyReportPageBody />
    </UniversalPage>
  );
}
